// src/ui/lazyRoute.ts
//
// Líně načítané obrazovky (úklid a rychlost, 12. 10. 2026): administrace,
// nastavení živého závodu, živé výsledky a tabule se stahují až při prvním
// otevření – úvodní bundle (přihlášení, přehled, výsledky, přihláška,
// obrazovka rozhodčího) je o to menší.
//
// Na rozdíl od React.lazy jde načtení po chybě zopakovat: React.lazy si
// zamítnutý import pamatuje navždy a „Zkusit znovu“ by jen znovu ukázalo
// chybu. Tady chybu vidí hranice RouteErrorBoundary (AppShell), a když se
// zkouší znovu (tlačítko, návrat připojení, automatický pokus tabule,
// navigace jinam nebo odchod z obrazovky), zavolá resetFailedLazyRoutes()
// a import proběhne znovu (webpack nepovedený chunk stáhne znovu).
//
// Service worker aplikace nemá: obrazovka, která se v této záložce ještě
// nestáhla, se bez připojení neotevře – proto obrazovka rozhodčího zůstává
// v hlavním bundlu a živé obrazovky se rozhodčím stahují předem
// (usePreloadWhenIdle ve spodní navigaci a na živých obrazovkách).

import React, { useEffect } from "react";

export interface LazyRouteComponent<P> extends React.FC<P> {
  /** Stáhne obrazovku předem. Chybu vrací – volající ji obvykle zahodí. */
  preload: () => Promise<void>;
}

/** Cokoli, co jde stáhnout předem (obrazovka, knihovna). */
export interface Preloadable {
  preload: () => Promise<void>;
}

/** Obnovy obrazovek, jejichž načtení selhalo při vykreslení. */
const failedResets = new Set<() => void>();

/**
 * Jak dlouho od předání hranici se chyba stažení drží bez resetu: React po
 * chybě vykreslení hned zkouší znovu (a ve StrictMode dvakrát) – nový import
 * místo chyby by vedl k nekonečné smyčce pokusů offline. Později už nové
 * vykreslení (hranice v chybě obrazovku nevykresluje, takže jen nové
 * otevření) stahuje znovu.
 */
export const LAZY_FAILURE_HOLD_MS = 2000;

/**
 * Chyba, kterou žádné vykreslení nepředalo (obrazovku mezitím zavřel – např.
 * Zpět během stahování), po této době zastará: další otevření stahuje znovu
 * místo staré chyby. Do té doby ji dostane nejbližší vykreslení, i když
 * přijde pozdě (zahlcené vlákno) – jinak by hrozila smyčka.
 */
export const LAZY_UNDELIVERED_FAILURE_MS = 30000;

/** Další vykreslení obrazovek, které se nestáhly, zkusí import znovu. */
export function resetFailedLazyRoutes(): void {
  failedResets.forEach((reset) => reset());
  failedResets.clear();
}

/**
 * Chyba stažení chunku (webpack ChunkLoadError, i CSS chunk) – typicky výpadek
 * sítě nebo nový deploy, po kterém starý chunk na serveru už není.
 */
export function isChunkLoadError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { name?: unknown; code?: unknown; message?: unknown };
  if (e.name === "ChunkLoadError" || e.code === "CSS_CHUNK_LOAD_FAILED") return true;
  return typeof e.message === "string" && /Loading (CSS )?chunk \S+ failed/i.test(e.message);
}

/**
 * Obrazovka načtená až při prvním vykreslení (pod <Suspense>). Chyba stažení
 * se při vykreslení vyhodí hranici a drží se LAZY_FAILURE_HOLD_MS od předání
 * (nebo do resetFailedLazyRoutes()). Chyba předběžného stažení (preload) se
 * nedrží vůbec.
 */
// Typ jako React.lazy: props obrazovky se převezmou z importované komponenty
export function lazyRoute<T extends React.ComponentType<any>>(
  load: () => Promise<{ default: T }>
): LazyRouteComponent<React.ComponentProps<T>> {
  type P = React.ComponentProps<T>;
  let Loaded: T | null = null;
  let pending: Promise<void> | null = null;
  // Na výsledek čeká vykreslení (vyhozený promise) – jen pak se chyba drží
  let renderWaiting = false;
  let failure: { error: unknown; rejectedAt: number; deliveredAt: number | null } | null = null;

  const reset = () => {
    failure = null;
  };

  const preload = (): Promise<void> => {
    if (Loaded) return Promise.resolve();
    if (!pending) {
      pending = load().then(
        (mod) => {
          Loaded = mod.default;
          pending = null;
          renderWaiting = false;
        },
        (error: unknown) => {
          pending = null;
          if (renderWaiting) {
            renderWaiting = false;
            failure = { error, rejectedAt: Date.now(), deliveredAt: null };
            failedResets.add(reset);
          }
          throw error;
        }
      );
    }
    return pending;
  };

  const Lazy = ((props: P) => {
    if (Loaded) return React.createElement(Loaded, props);
    if (failure) {
      const now = Date.now();
      if (failure.deliveredAt === null && now - failure.rejectedAt < LAZY_UNDELIVERED_FAILURE_MS) {
        failure.deliveredAt = now;
      }
      if (failure.deliveredAt !== null && now - failure.deliveredAt < LAZY_FAILURE_HOLD_MS) {
        throw failure.error;
      }
      failure = null;
    }
    const wait = preload();
    renderWaiting = true;
    throw wait;
  }) as LazyRouteComponent<P>;
  Lazy.preload = preload;
  return Lazy;
}

/**
 * Stáhne obrazovky předem, až bude prohlížeč volný (requestIdleCallback,
 * jinak za 2 s) – bez vlivu na první vykreslení. Chyby se zahazují (offline
 * se obrazovka při otevření zkusí stáhnout znovu). Vrací zrušení.
 */
export function preloadWhenIdle(screens: ReadonlyArray<Preloadable>): () => void {
  if (typeof window === "undefined") return () => undefined;
  const run = () => {
    screens.forEach((screen) => {
      screen.preload().catch(() => undefined);
    });
  };
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (typeof w.requestIdleCallback === "function") {
    const id = w.requestIdleCallback(run, { timeout: 5000 });
    return () => {
      if (typeof w.cancelIdleCallback === "function") w.cancelIdleCallback(id);
    };
  }
  const timer = window.setTimeout(run, 2000);
  return () => window.clearTimeout(timer);
}

/**
 * Hook: dokud je komponenta připojená a `enabled`, stáhne obrazovky předem
 * (preloadWhenIdle) a po každém návratu připojení to zkusí znovu – stažené
 * obrazovky se nestahují dvakrát (preload je pak bez požadavku).
 * `screens` musí být stálé (konstanta modulu).
 */
export function usePreloadWhenIdle(screens: ReadonlyArray<Preloadable>, enabled: boolean = true): void {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;
    let cancel = preloadWhenIdle(screens);
    const again = () => {
      cancel();
      cancel = preloadWhenIdle(screens);
    };
    window.addEventListener("online", again);
    return () => {
      cancel();
      window.removeEventListener("online", again);
    };
  }, [screens, enabled]);
}
